import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Paper,
  IconButton,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { renderRubricTable , renderFrameworkRubricTable } from './RubricTables';
import { RubricDetails } from './RubricDetails';
import { ScoringRubricItem } from '@/types';


interface RubricModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  frameWorkRubric: { category_name: string; is_completed: boolean;}[];
  userRubric: ScoringRubricItem[];
}

// Custom TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const RubricModal: React.FC<RubricModalProps> = ({ open, onOpenChange, userRubric , frameWorkRubric }) => {
  const [tabValue, setTabValue] = useState(0);
  
  const userTotalPossiblePoints = userRubric.reduce((sum, item) => sum + item.totalPoints, 0);
  const userTotalScoredPoints = userRubric.reduce((sum, item) => sum + item.pointsScored, 0);

  const frameWorkTotalPossiblePoints = frameWorkRubric.length;
  const frameWorkTotalScoredPoints = frameWorkRubric.filter(item => item.is_completed).length;

  // Reset tab value when user rubric changes
  useEffect(() => {
    if (userRubric.length === 0) {
      setTabValue(0);
    }
  }, [userRubric.length]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Determine tab configuration based on available data
  const hasUserRubric = userRubric.length > 0;
  const tabConfig = hasUserRubric 
    ? [
        { label: "Overview", index: 0 },
        { label: "Details", index: 1 },
      ]
    : [
        { label: "Framework Categories", index: 0 }
      ];

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
          color: 'white',
          maxHeight: '80vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: 1,         
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" component="div" sx={{ color: 'text.primary' }}>
          Assessment Rubric
        </Typography>
        <IconButton 
          onClick={handleClose}
          sx={{ color: 'text.primary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 3 , mt:2}}>
          <Paper sx={{ 
            bgcolor:'background.paper',
            p: 2,
            borderRadius: 2
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' ,
            }}>
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                Rubric Score
              </Typography>
              <Typography variant="h5" sx={{ 
                color: 'primary.main',
                fontWeight: 'bold' 
              }}>
                {hasUserRubric && (tabValue === 0 || tabValue === 1)
                  ? `${userTotalScoredPoints}/${userTotalPossiblePoints} (${Math.round((userTotalScoredPoints / userTotalPossiblePoints) * 100)}%)` 
                  : `${frameWorkTotalScoredPoints}/${frameWorkTotalPossiblePoints} (${Math.round((frameWorkTotalScoredPoints / frameWorkTotalPossiblePoints) * 100)}%)`
                }
              </Typography>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
                  sx={{
                  width: '100%',
                  minHeight: 'unset',
                  '& .MuiTabs-flexContainer': {
                    bgcolor: 'background.paper', // bg-slate-800
                    borderRadius: '6px',
                    padding: '4px',
                    gap: '4px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr', // grid-cols-2 1fr 1fr or grid-cols-1 1fr
                    width: '100%',
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none', // Hide the default indicator
                  },
                  '& .MuiTab-root': {
                    minHeight: '40px',
                    padding: '8px 16px',
                    color: 'text.primary', // text-slate-300
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '4px',
                    transition: 'all 0.2s',
                    width: '100%',
                    maxWidth: 'none',
                    '&:hover': {
                      bgcolor: 'rgba(51, 65, 85, 0.5)',
                    },
                    '&.Mui-selected': {
                      bgcolor: 'background.paper', // bg-slate-700
                      color: 'primary.main', // text-white
                    },
                  },
                }}
            >
              {tabConfig.map((tab) => (
                <Tab key={tab.index} label={tab.label} />
              ))}
            </Tabs>
          </Box>
          
          {/* Overview Tab */}
          {hasUserRubric && (
            <CustomTabPanel value={tabValue} index={0}>
              {renderRubricTable(userRubric)}
            </CustomTabPanel>
          )}
          
          {/* Details Tab */}
          {hasUserRubric && (
            <CustomTabPanel value={tabValue} index={1}>
              <RubricDetails userRubric={userRubric} />
            </CustomTabPanel>
          )}
        </Box>

        <Box sx={{ mt: 3 }}>
          <Alert 
            severity="info" 
            icon={<CheckIcon />}
            sx={{ 
              bgcolor: 'background.paper',
              color: 'text.primary',
              '& .MuiAlert-icon': {
                color: 'primary.main'
              }
            }}
          >
            Only categories with 100% are considered completed
          </Alert>
        </Box>
      </DialogContent>
    </Dialog>
  );
};